/**
 * @file IntegrationStatusList.tsx
 * @description Блок «Состояние интеграций» на «Обзоре» платформы
 * (phase19.29): настоящие статусы экземпляров из `platformIntegrations.list`.
 *
 * Сначала проблемные (ошибка → пауза → остальные), статус — «пилюля» из
 * общего реестра `INTEGRATION_STATUS_META`. Загрузка — скелет, ошибка —
 * текст и «Повторить», пусто — «ещё не подключены» со ссылкой в раздел.
 */

import { ChevronRight } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";
import { INTEGRATION_STATUS_META } from "../status-meta";
import type { OverviewSource } from "./use-overview-source";
import {
  INTEGRATION_KIND_META,
  type PlatformIntegrationDto,
} from "../../../lib/api-platform-integrations";

/** Порядок: проблемы наверху. */
const STATUS_ORDER: Record<PlatformIntegrationDto["status"], number> = {
  ERROR: 0,
  PAUSED: 1,
  NOT_CONFIGURED: 2,
  ACTIVE: 3,
};

interface Props {
  source: OverviewSource<PlatformIntegrationDto[]>;
  onRetry: () => void;
}

export function IntegrationStatusList({ source, onRetry }: Props) {
  return (
    <section aria-labelledby="overview-integrations" className="flex flex-col gap-3">
      <h2 id="overview-integrations" className="type-section-title text-on-surface">
        Состояние интеграций
      </h2>
      <div className="overflow-hidden rounded-xl bg-card ring-1 ring-outline-variant">
        <Body source={source} onRetry={onRetry} />
        <Link
          to="/platform/integrations"
          className="flex min-h-12 items-center justify-between border-t border-outline-variant px-4 text-sm font-medium text-primary outline-none hover:bg-surface-low focus-visible:bg-surface-low"
        >
          Все интеграции
          <ChevronRight className="size-4" aria-hidden />
        </Link>
      </div>
    </section>
  );
}

function Body({ source, onRetry }: Props) {
  if (source.status === "error") {
    return (
      <div className="flex flex-col items-start gap-3 p-4">
        <p className="text-sm text-on-surface">{source.message}</p>
        <Button variant="soft" onClick={onRetry}>
          Повторить
        </Button>
      </div>
    );
  }
  if (source.status !== "ready") {
    return (
      <div className="flex flex-col gap-3 p-4" aria-label="Загрузка">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-2/3" />
      </div>
    );
  }
  if (source.data.length === 0) {
    return (
      <p className="p-4 text-sm text-on-surface-variant">Интеграции ещё не подключены.</p>
    );
  }

  const sorted = [...source.data].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
  return (
    <ul className="divide-y divide-outline-variant">
      {sorted.map((item) => {
        const status = INTEGRATION_STATUS_META[item.status];
        return (
          <li key={item.id} className="flex min-h-12 items-center gap-3 px-4 py-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-on-surface">
                {INTEGRATION_KIND_META[item.kind].label}
              </div>
              <div className="truncate text-xs text-on-surface-variant">
                {item.name}
                {item.shop ? ` · ${item.shop.displayName}` : " · все магазины"}
              </div>
            </div>
            <Badge variant={status.variant}>{status.label}</Badge>
          </li>
        );
      })}
    </ul>
  );
}
